import { API_BASE_URL } from '../config/api';

// Типы для API конфигурации
export interface Game {
  id: string;
  name: string;
  icon: string;
  requiresAuth: boolean;
  isNew?: boolean; // бэдж «Новинка» (games.new_until на сервере)
}

export interface GamesByCategory {
  [category: string]: Game[];
}

export interface TimedMode {
  id: string;
  type: 'timed';
  name: string;
  timeLimit: number;
  cardCount: number;
  minBet: number;
  maxBet?: number;
  betMultiplicity: number;
  hasRatings: boolean;
  goal?: string;
  instructions?: string;
  showBet?: boolean;
  showGoal?: boolean;
  showTimeLimit?: boolean;
  showCards?: boolean;
  // Специфичные параметры
  maxToNumber?: number;
  chipCounts?: number[];
  handsCount?: number;
  withStake?: boolean; // «Комплит — выплата»: режим с возвратом ставки
  maxNumber?: number; // таблица умножения: наибольшее множимое
  multipliers?: number[]; // таблица умножения: множители уровня
  bets?: string[]; // «Покер: бонусы»: побочные ставки уровня
  combinations?: string[]; // блэкджек: коды комбинаций уровня
  tables?: Record<string, Array<{ code: string; coefficient: number }>>; // «Покер: бонусы»: таблицы выплат
}

export interface CustomMode {
  id: string;
  type: 'custom';
  name: string;
  timeLimit: null;
  hasRatings: false;
  goal?: string;
  instructions?: string;
  betMultiplicityOptions: number[];
  showBet?: boolean;
  showGoal?: boolean;
  showTimeLimit?: boolean;
  showCards?: boolean;
  // Опции для разных игр
  maxBetOptions?: number[];
  maxToNumberOptions?: number[];
  chipCountOptions?: number[];
  pictureOptions?: string[];
  maxNumberOptions?: number[];
  multiplierOptions?: number[];
  // Новые игры
  answerOptions?: string[];
  completesOptions?: number[];
  maxUnitOptions?: number[];
  unitStepOptions?: number[];
  numbersOptions?: string[];
  cardsOptions?: string[];
  hardOptions?: string[];
  edgeOptions?: string[];
  hintOptions?: string[];
  maxStraightOptions?: number[];
  betTypeOptions?: string[]; // «Покер: бонусы»: побочные ставки
  touchOptions?: string[];
  withStakeOptions?: string[];
}

export type GameMode = TimedMode | CustomMode;

export interface GameDataConfig {
  config: {
    title: string;
    time: number | null;
    timelimit: string;
    goal: string;
    bet: string;
    numberOfBets: number;
  };
  combinations: any[];
}

// API функции для работы с конфигурацией
export const configAPI = {
  // Helper to get current language
  getCurrentLanguage(): string {
    return localStorage.getItem('i18nextLng') || 'en';
  },

  // Получить список всех игр
  async getGames(): Promise<{ gamesByCategory: GamesByCategory }> {
    // Ответ, который boot.js запросил ещё до загрузки приложения (используется один раз). Берём его, только если он
    // на том же языке, что выбрал i18next: иначе названия игр сохранились бы в кэш другого языка
    const lang = this.getCurrentLanguage();
    const boot = window as unknown as { __earlyGames?: Promise<{ gamesByCategory: GamesByCategory } | null>; __earlyGamesLanguage?: string };
    const early = boot.__earlyGames;
    if (early) {
      const sameLanguage = !boot.__earlyGamesLanguage || boot.__earlyGamesLanguage === lang.slice(0, 2).toLowerCase();
      delete boot.__earlyGames;
      delete boot.__earlyGamesLanguage;
      const data = sameLanguage ? await early : null;
      if (data?.gamesByCategory) return data;
    }
    const response = await fetch(`${API_BASE_URL}/config/games`, {
      headers: {
        'Accept-Language': lang,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch games');
    }

    return response.json();
  },

  // Получить режимы игры
  async getGameModes(gameId: string): Promise<{ modes: GameMode[] }> {
    const lang = this.getCurrentLanguage();
    const response = await fetch(`${API_BASE_URL}/config/games/${gameId}/modes`, {
      headers: {
        'Accept-Language': lang,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch game modes');
    }

    return response.json();
  },

  // Получить конкретный режим
  async getGameMode(gameId: string, modeId: string): Promise<{ mode: GameMode }> {
    const lang = this.getCurrentLanguage();
    const response = await fetch(`${API_BASE_URL}/config/games/${gameId}/modes/${modeId}`, {
      headers: {
        'Accept-Language': lang,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch game mode');
    }

    return response.json();
  },
};
